export type RaptorAbGroup = 'control' | 'experiment';

export type ResolveRaptorAbTestOptions = {
  userId?: string;
  sessionId?: string;
  search?: string;
};

const RAPTOR_AB_QUERY_KEY = 'raptor_ab';
const RAPTOR_AB_STORAGE_KEY = '__weee_raptor_ab_group__';
const RAPTOR_AB_SEED_KEY = '__weee_raptor_ab_seed__';
const RAPTOR_SCRIPT_HOST = 'https://static.weeecdn.com/common/raptor';

export const RAPTOR_GROUP_CONTROL: RaptorAbGroup = 'control';
export const RAPTOR_GROUP_EXPERIMENT: RaptorAbGroup = 'experiment';

const RAPTOR_METRICS_CONTROL_VERSION = '0.0.8';
const RAPTOR_METRICS_EXPERIMENT_VERSION = '0.1.0';
const RAPTOR_EXCEPTION_VERSION = '0.0.8';
const RAPTOR_METRICS_SCRIPT_SRC_OVERRIDE = process.env.NEXT_PUBLIC_RAPTOR_METRICS_SCRIPT_SRC_OVERRIDE?.trim();

export const RAPTOR_EXCEPTION_SCRIPT_SRC = `${RAPTOR_SCRIPT_HOST}/exceptions.iife_${RAPTOR_EXCEPTION_VERSION}.js`;

const buildRaptorScriptSrc = (version: string) =>
  RAPTOR_METRICS_SCRIPT_SRC_OVERRIDE || `${RAPTOR_SCRIPT_HOST}/index.umd_${version}.js`;

const parseRaptorAbGroup = (value?: string | null): RaptorAbGroup | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.toLowerCase();
  if (normalizedValue === RAPTOR_GROUP_CONTROL || normalizedValue === RAPTOR_GROUP_EXPERIMENT) {
    return normalizedValue;
  }

  return null;
};

const getPersistedValue = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const setPersistedValue = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (_error) {
    // noop
  }
};

const hashTo100Bucket = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 100;
  }

  return Math.abs(hash);
};

const getStableSeed = (userId?: string, sessionId?: string) => {
  if (userId) {
    return userId;
  }

  if (sessionId) {
    return sessionId;
  }

  const persistedSeed = getPersistedValue(RAPTOR_AB_SEED_KEY);
  if (persistedSeed) {
    return persistedSeed;
  }

  const randomSeed = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  setPersistedValue(RAPTOR_AB_SEED_KEY, randomSeed);
  return randomSeed;
};

export const resolveRaptorAbTest = (options: ResolveRaptorAbTestOptions = {}) => {
  const queryGroup = parseRaptorAbGroup(
    new URLSearchParams(options.search ?? window.location.search).get(RAPTOR_AB_QUERY_KEY)
  );
  if (queryGroup) {
    setPersistedValue(RAPTOR_AB_STORAGE_KEY, queryGroup);
  }

  const persistedGroup = parseRaptorAbGroup(getPersistedValue(RAPTOR_AB_STORAGE_KEY));
  const finalGroup =
    queryGroup ??
    persistedGroup ??
    (hashTo100Bucket(getStableSeed(options.userId, options.sessionId)) < 50
      ? RAPTOR_GROUP_CONTROL
      : RAPTOR_GROUP_EXPERIMENT);

  const version =
    finalGroup === RAPTOR_GROUP_EXPERIMENT ? RAPTOR_METRICS_EXPERIMENT_VERSION : RAPTOR_METRICS_CONTROL_VERSION;

  setPersistedValue(RAPTOR_AB_STORAGE_KEY, finalGroup);

  return {
    group: finalGroup,
    version,
    scriptSrc: buildRaptorScriptSrc(version),
    enablePathAttribution: finalGroup === RAPTOR_GROUP_EXPERIMENT
  };
};
